import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Mail, Phone, MapPin, FileText, Upload, Trash2,
  DollarSign, TrendingUp, Briefcase, Activity, Download, Link as LinkIcon,
  QrCode, Copy, Printer,
} from "lucide-react";

const DOC_TYPES = [
  { key: "w9", label: "W-9 Form" },
  { key: "coi", label: "Certificate of Insurance (COI)" },
  { key: "agreement", label: "Service Agreement" },
];

export default function MasterBodyShopDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const qrRef = useRef(null);
  const fileRefs = useRef({});

  const load = () => api.get(`/master/body-shops/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);

  const upload = async (docType, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("file", file);
    try {
      await api.post(`/master/body-shops/${id}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${docType.toUpperCase()} uploaded`);
      load();
    } catch (e) { toast.error("Upload failed"); }
  };

  const removeDoc = async (docType) => {
    if (!window.confirm(`Remove ${docType.toUpperCase()}?`)) return;
    await api.delete(`/master/body-shops/${id}/document/${docType}`);
    toast.success("Removed"); load();
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const bs = data.body_shop;
  const k = data.kpis;
  const publicUrl = `${window.location.origin}/submit/${bs.submission_token || bs.slug || ""}`;
  const shopUrl = bs.slug ? `${window.location.origin}/shop/${bs.slug}` : null;

  const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); };
  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `qr-${bs.slug || bs.id}.png`;
    a.click();
    toast.success("QR Code downloaded");
  };
  const printQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const w = window.open("", "_blank", "width=600,height=800");
    w.document.write(`
      <html><head><title>QR — ${bs.company_name}</title></head>
      <body style="font-family:Arial;text-align:center;padding:40px;">
        <h1 style="margin-bottom:8px">${bs.company_name}</h1>
        <p style="color:#666;margin-top:0">TrueLine Body Shop Portal</p>
        <img src="${dataUrl}" style="width:380px;height:380px;margin:24px 0" />
        <p style="font-family:monospace;font-size:13px;color:#444;word-break:break-all">${publicUrl}</p>
        <p style="margin-top:32px;color:#888;font-size:11px">Scan to submit estimates, track jobs and pay commission invoices</p>
        <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
      </body></html>`);
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="bs-detail-page">
      <Link to="/master/body-shops" className="text-xs text-slate-500 hover:text-blue-400 flex items-center gap-1 w-fit"><ArrowLeft className="w-3 h-3" /> Back to Body Shops</Link>

      <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-[#0B1120] to-[#0A0F1C] p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-blue-600/15 border border-blue-500/20 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-3xl font-semibold tracking-tight">{bs.company_name}</h1>
            <div className="text-sm text-slate-400 mt-1">{bs.owner_name || "—"}</div>
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-500">
              {bs.email && <span className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{bs.email}</span>}
              {bs.phone && <span className="flex items-center gap-1.5 font-mono"><Phone className="w-3 h-3" />{bs.phone}</span>}
              {(bs.city || bs.state) && <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{[bs.address, bs.city, bs.state, bs.zip_code].filter(Boolean).join(", ")}</span>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <Badge className="bg-blue-500/15 text-blue-300 border-blue-500/20">{bs.commission_pct}% commission</Badge>
            <div className="text-xs text-slate-500 mt-1.5">{bs.payment_terms}</div>
            <div className="flex gap-2 mt-3 justify-end">
              <Button
                data-testid="bs-qr-btn"
                size="sm"
                onClick={() => setShowQR(true)}
                className="bg-blue-600 hover:bg-blue-500 h-8 text-xs"
              ><QrCode className="w-3.5 h-3.5 mr-1.5" />QR Code</Button>
              {bs.slug && (
                <a
                  href={`/shop/${bs.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="bs-public-link"
                  className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-slate-700 bg-slate-900/40 text-xs text-slate-300 hover:border-blue-500/40 hover:text-blue-300"
                ><LinkIcon className="w-3 h-3" />Open Page</a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Jobs Total" value={k.jobs_total} icon={Briefcase} />
        <Kpi label="This Month" value={k.jobs_this_month} icon={TrendingUp} />
        <Kpi label="Revenue Generated" value={`$${k.revenue_generated.toLocaleString()}`} icon={DollarSign} accent="emerald" />
        <Kpi label="Commission Total" value={`$${k.commission_total.toLocaleString()}`} icon={DollarSign} accent="blue" />
        <Kpi label="Commission Pending" value={`$${k.commission_pending.toLocaleString()}`} icon={DollarSign} accent="amber" />
        <Kpi label="Approval Rate" value={`${k.approval_rate}%`} icon={TrendingUp} accent="cyan" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Recent Jobs</div>
          {data.jobs.length === 0 ? (
            <div className="text-center text-slate-500 py-8 text-sm">No jobs yet</div>
          ) : (
            <div className="divide-y divide-slate-800/60">
              {data.jobs.slice(0, 10).map((j) => (
                <Link key={j.id} to={`/jobs/${j.id}`} className="flex items-center gap-3 py-2.5 hover:bg-slate-900/30 px-2 rounded-lg transition-colors">
                  <div className="font-mono text-xs text-blue-300 w-32 truncate">{j.vin}</div>
                  <div className="flex-1 text-sm truncate">{j.customer_name}</div>
                  <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[10px]">{j.status}</Badge>
                  <div className="text-xs font-mono text-slate-500 w-24 text-right">${(j.total_amount || 0).toLocaleString()}</div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3 flex items-center gap-1.5"><FileText className="w-3 h-3" />Documents</div>
          <div className="space-y-2">
            {DOC_TYPES.map((d) => {
              const url = bs[`${d.key}_url`];
              const filename = bs[`${d.key}_filename`];
              return (
                <div key={d.key} data-testid={`doc-${d.key}`} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                  <div className="text-xs font-medium text-slate-200">{d.label}</div>
                  {url ? (
                    <div className="mt-2 flex items-center gap-2">
                      <a href={url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-400 truncate flex-1 flex items-center gap-1"><Download className="w-3 h-3" />{filename || "Download"}</a>
                      <button data-testid={`doc-delete-${d.key}`} onClick={() => removeDoc(d.key)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ) : (
                    <>
                      <input
                        ref={(el) => (fileRefs.current[d.key] = el)}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        className="hidden"
                        onChange={(e) => upload(d.key, e.target.files?.[0])}
                        data-testid={`doc-upload-input-${d.key}`}
                      />
                      <Button
                        data-testid={`doc-upload-btn-${d.key}`}
                        variant="outline"
                        size="sm"
                        onClick={() => fileRefs.current[d.key]?.click()}
                        className="mt-2 w-full border-slate-700 bg-slate-900/60 text-xs h-7"
                      ><Upload className="w-3 h-3 mr-1.5" />Upload</Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3 flex items-center gap-1.5"><Activity className="w-3 h-3" />Activity Log</div>
        {data.activity.length === 0 ? (
          <div className="text-center text-slate-500 py-6 text-sm">No activity yet</div>
        ) : (
          <div className="divide-y divide-slate-800/60">
            {data.activity.slice(0, 15).map((a) => (
              <div key={a.id} className="flex items-center gap-3 py-2 text-xs">
                <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[10px]">{a.action}</Badge>
                <span className="text-slate-400 flex-1">{a.actor_name}</span>
                <span className="font-mono text-slate-600">{new Date(a.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="bg-[#0B1120] border-slate-800 max-w-md" data-testid="qr-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5 text-blue-400" />{bs.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div ref={qrRef} className="flex justify-center bg-white rounded-xl p-6">
              <QRCodeCanvas value={publicUrl} size={260} level="H" includeMargin={true} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Public Submission Link</div>
              <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-2.5 font-mono text-[11px] text-blue-300 break-all">{publicUrl}</div>
            </div>
            {shopUrl && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Friendly URL (slug)</div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-2.5 font-mono text-[11px] text-cyan-300 break-all">{shopUrl}</div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 pt-2">
              <Button data-testid="qr-download-btn" onClick={downloadQR} className="bg-blue-600 hover:bg-blue-500"><Download className="w-4 h-4 mr-1.5" />PNG</Button>
              <Button data-testid="qr-print-btn" onClick={printQR} variant="outline" className="border-slate-700 bg-slate-900/40"><Printer className="w-4 h-4 mr-1.5" />Print</Button>
              <Button data-testid="qr-copy-btn" onClick={copyLink} variant="outline" className="border-slate-700 bg-slate-900/40"><Copy className="w-4 h-4 mr-1.5" />Copy</Button>
            </div>
            <div className="text-[10px] text-slate-500 text-center leading-relaxed pt-2 border-t border-slate-800">
              Print this and pin it at the body shop reception · clients scan to submit estimates, track jobs and pay commissions
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Kpi = ({ label, value, icon: Icon, accent = "blue" }) => {
  const accentClass = {
    blue: "text-blue-400 bg-blue-500/10",
    emerald: "text-emerald-400 bg-emerald-500/10",
    amber: "text-amber-400 bg-amber-500/10",
    cyan: "text-cyan-400 bg-cyan-500/10",
  }[accent];
  return (
    <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
        <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${accentClass}`}><Icon className="w-3 h-3" /></div>
      </div>
      <div className="font-display text-xl font-semibold tracking-tight">{value}</div>
    </div>
  );
};
